// SelectWidget for every config form: a single-choice field renders as the
// portal's styled react-select instead of the browser's native select. When its
// options carry a description, the open menu shows each one under its label.
// Options carry descriptions when a runner writes the field as
// `oneOf: [{const, title, description}]` — rjsf hands each option's subschema to
// the widget as `enumOptions[i].schema`.
//
// Registered as the form-wide SelectWidget rather than per field through
// generateUISchema: fields behind `allOf`/`if`/`then` (the GFW confidence
// levels) never pass through generateUISchema, but rjsf still renders them with
// the registry's SelectWidget.
//
// Multi-selects keep rjsf's stock widget.
import React from 'react';
import Select from 'react-select';
import { WidgetProps, enumOptionsIsSelected } from '@rjsf/utils';
import { getDefaultRegistry } from '@rjsf/core';
import { useTranslation } from 'react-i18next';
import { portaledMenuProps } from 'styles/componentstyles/selectinputstyles';
import { useEnterOpensMenu } from 'shared/components/inputs/useEnterOpensMenu';

type DescribedOption = {
  value: any;
  label: string;
  description?: string;
};

// The stock widget has to come from rjsf's defaults, since the registry's
// SelectWidget is this widget. Built on first use rather than at import:
// building the default registry is not free, and tests that mock @rjsf/core
// don't provide getDefaultRegistry.
let defaultSelectWidget: React.ComponentType<WidgetProps> | undefined;
const getDefaultSelectWidget = () =>
  (defaultSelectWidget ??= getDefaultRegistry().widgets.SelectWidget);

const DescribedSelectWidget = (props: WidgetProps) => {
  const {
    id,
    options,
    value,
    required,
    disabled,
    readonly,
    placeholder,
    autofocus,
    onChange,
    onBlur,
    onFocus,
  } = props;
  const { t } = useTranslation();
  const { selectRef, handleKeyDown } = useEnterOpensMenu(false);

  if (props.multiple) {
    const DefaultSelectWidget = getDefaultSelectWidget();
    return <DefaultSelectWidget {...props} />;
  }

  const selectOptions: DescribedOption[] = (options.enumOptions ?? []).map(
    (option: any) => ({
      value: option.value,
      label: option.label,
      description: option.schema?.description,
    }),
  );
  // rjsf's deep equality, as the stock widget uses: a `const` that is an
  // object or array still matches an equal saved value.
  const selected = selectOptions.find((option) =>
    enumOptionsIsSelected(option.value, value),
  );
  const enumDisabled: any[] = options.enumDisabled ?? [];

  return (
    <Select
      ref={selectRef}
      onKeyDown={handleKeyDown}
      inputId={id}
      isSearchable={false}
      isClearable={false}
      isDisabled={disabled || readonly}
      autoFocus={autofocus}
      options={selectOptions}
      isOptionDisabled={(option: DescribedOption) =>
        enumOptionsIsSelected(option.value, enumDisabled)
      }
      value={selected ?? null}
      // Only an unset placeholder falls back to "Select"; an explicit '' stays
      // empty, as in the stock widget.
      placeholder={placeholder ?? t('Select')}
      onChange={(option: DescribedOption | null) =>
        onChange(option ? option.value : options.emptyValue)
      }
      onBlur={() => onBlur(id, value)}
      onFocus={() => onFocus(id, value)}
      // The closed control shows the label alone; the open menu adds the
      // description under it.
      formatOptionLabel={(option: DescribedOption, { context }) =>
        context === 'menu' && option.description ? (
          <div data-testid="DescribedSelectWidget-option">
            <div>{option.label}</div>
            <div
              className="mt-0.5 text-body-sm leading-snug opacity-70"
              data-testid="DescribedSelectWidget-description"
            >
              {option.description}
            </div>
          </div>
        ) : (
          option.label
        )
      }
      {...portaledMenuProps()}
    />
  );
};

export default DescribedSelectWidget;
